import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import axios, { AxiosError } from "axios"
import { getBaseEndpoint } from "../../features/endpoints/endpointManager"

interface UserRoleRecord {
  id: string
  name: string
  email: string
  userRoles: string[]
}

type LoadingStatus = "initial" | "loading" | "loaded" | "error"

interface UserRoleData {
  userList: UserRoleRecord[]
}

interface UserRoleState {
  dataStatus: LoadingStatus
  data: UserRoleData
}

const initialState: UserRoleState = {
  dataStatus: "initial",
  data: {
    userList: [],
  },
}

const getUsersEndpoint = (): string => {
  return getBaseEndpoint() + `/account/users`
}

const getUserRolesEndpoint = (): string => {
  return getBaseEndpoint() + `/account/userRoles`
}

export const loadUserList = createAsyncThunk(
  "userRoles/getList",
  async (userId: string, { dispatch }) => {
    dispatch(userRolesSlice.actions.dataStatusUpdated("loading"))
    try {
      const url = getUsersEndpoint() + "?Not=1&UserRole=role-affiliate"
      const response = await axios.get(url)

      if (response.status === 200) {
        const payload: UserRoleRecord[] = []
        const data: [] = response.data.$values
        data.forEach((item: any) => {
          payload.push({
            id: item.id,
            name: `${item.givenName} ${item.surname}`,
            email: item.email,
            userRoles: item.userRoles.$values,
          })
        })
        dispatch(userRolesSlice.actions.dataUpdated(payload))
        dispatch(userRolesSlice.actions.dataStatusUpdated("loaded"))
      }
    } catch (error) {
      if (error && (error as AxiosError).response?.status === 404) {
        dispatch(userRolesSlice.actions.dataStatusUpdated("loaded"))
      } else {
        console.error(error)
        dispatch(userRolesSlice.actions.dataStatusUpdated("error"))
      }
    }
  },
)

export const updateUserRoles = createAsyncThunk(
  "userRoles/updateUserRoles",
  async (payload: { userId: string; userRoles: string[] }, { dispatch }) => {
    try {
      const url = getUserRolesEndpoint()
      const response = await axios.put(url, payload)

      // refresh list after update
      if (response.status === 204) {
        dispatch(loadUserList(payload.userId))
      }
    } catch (error) {
      console.error(error)
    }
  },
)

const userRolesSlice = createSlice({
  name: "userRoles",
  initialState,
  reducers: {
    dataStatusUpdated: (state, { payload }: PayloadAction<LoadingStatus>) => {
      state.dataStatus = payload
    },
    dataUpdated: (state, { payload }: PayloadAction<UserRoleRecord[]>) => {
      state.data.userList = payload
    },
  },
})

export const { dataStatusUpdated, dataUpdated } = userRolesSlice.actions

export const selectDataStatus = (state: any) => state.userRoles.dataStatus

export const selectUserList = (state: any) => state.userRoles.data.userList

export default userRolesSlice.reducer
